import { Component, Inject, OnInit } from '@angular/core';
import { ILabels } from "libraries/interfaces/ILabels";
import { Yii } from "libraries/interfaces/Yii";
import { MAT_DIALOG_DATA, MatDialog } from "@angular/material/dialog";
import { EPropertyXCode, IBookingPropertyDescription, IPropertyXCodeAndXApply } from "libraries/service/resources/booking/interfaces/IGetBookings";
import { LanguageService } from "libraries/service/language/language.service";
import { BtnStyleTypes } from "libraries/ui_button/btn.component";
import { HtmlizePipe } from 'libraries/pipes/htmlize/htmlize.pipe';
import { PropertyDescriptionService } from "libraries/component/booking-information/components/sectors/components/sectors-stay/property-description.service";

export interface IArticle {
    header: string;
    paragraph: string[];
}

export interface IFirstInformation {
    checkIn: string;
    checkOut: string;
    goodToKnow?: string;
}

@Component({
    selector: 'important-information',
    template: `
        <card>
            <div class="card-header-content" mat-dialog-title>

                <div>
                    <h1>{{wording.title}}</h1>
                </div>

                <btn [type]="btnType"
                     [label]="wording.closeDialog"
                     (click)="closeDialog()">
                </btn>
            </div>

            <div class="card-body-content" mat-dialog-content>
                <div class="scroll">
                    <article class="firstInformation" *ngIf="hasCheckInformation">
                        <div *ngIf="firstInformation.checkIn">
                            <h2>{{wording.checkIn}}</h2>
                            <div [innerHTML]="firstInformation.checkIn"></div>
                        </div>
                        <div *ngIf="firstInformation.checkOut">
                            <h2>{{wording.checkOut}}</h2>
                            <div [innerHTML]="firstInformation.checkOut"></div>
                        </div>
                    </article>

                    <article *ngFor="let article of articles; trackBy: trackByFn">
                        <h2>{{article.header}}</h2>
                        <ng-container *ngFor="let p of article.paragraph; trackBy: trackByFn">
                            <div [innerHTML]="p"></div>
                        </ng-container>
                    </article>
                </div>

            </div>
        </card>

    `,
    styles: [`


        .firstInformation {
            display: flex;
        }

        .firstInformation div {
            flex: 1;
            margin-right: .75rem;
        }
        .firstInformation div:last-of-type {
            margin-right: 0;
        }
        .scroll{
            overflow: auto;
            max-height: 60vh;
        }

        article {
            margin: .75rem;
        }

        h2 {
            font-size: 1.2rem;
            margin: 1.5rem 0 .75rem;
        }

        article:first-child h2 {
            margin-top: 0
        }
    `],
})

export class ImportantInformationComponent implements OnInit {

    public articles: IArticle[];

    public firstInformation: IFirstInformation;

    public btnType: BtnStyleTypes = BtnStyleTypes.actionDo;

    public hasCheckInformation: boolean;

    public wording: ILabels = {
        title: Yii.t('wui-shared-js', 'Informations importantes'),
        closeDialog: Yii.t('wui-shared-js', 'Fermer'),
        checkIn: Yii.t('wui-shared-js', 'Arrivée'),
        checkOut: Yii.t('wui-shared-js', 'Départ'),
        goodToKnow: Yii.t('wui-shared-js', 'Bon à savoir'),
        Mandatory: Yii.t('wui-shared-js', 'Obligatoire'),
        Optional: Yii.t('wui-shared-js', 'Facultatif'),
    };

    constructor(
        @Inject(MAT_DIALOG_DATA) public data: IBookingPropertyDescription[],
        public dialog: MatDialog,
        public readonly languageService: LanguageService,
        private readonly htmlizePipe: HtmlizePipe,
        private readonly propertyDescriptionService: PropertyDescriptionService,
    ) {
    }

    public ngOnInit(): void {
        const language: string = this.languageService.getUserLanguage();
        if (!Array.isArray(this.data)) {
            this.data = [this.data];
        }

        const checkIn: IBookingPropertyDescription = this.propertyDescriptionService.findByXCode(this.data, EPropertyXCode.checkIn);
        const checkOut: IBookingPropertyDescription = this.propertyDescriptionService.findByXCode(this.data, EPropertyXCode.checkOut);
        this.firstInformation = {
            checkIn: (checkIn && checkIn.textLines ? checkIn.textLines : [])[0],
            checkOut: (checkOut && checkOut.textLines ? checkOut.textLines : [])[0],
        };

        this.hasCheckInformation = Boolean(this.firstInformation.checkIn || this.firstInformation.checkOut);
        // we try to get localized descriptions in user's language
        let localizedDescriptions: IBookingPropertyDescription[] =
            this.data.filter((description: IBookingPropertyDescription) => description.languageCode.toLowerCase() === language.toLowerCase());
        // if we have no localized descriptions, we fallback on english
        localizedDescriptions = localizedDescriptions.length ?
            localizedDescriptions : this.data.filter((description: IBookingPropertyDescription) => description.languageCode.toLowerCase() === 'en');

        this.articles = localizedDescriptions
            .filter((description: IBookingPropertyDescription) => this.propertyDescriptionService.isDisplayableDescription(description))
            .sort((a: IBookingPropertyDescription, b: IBookingPropertyDescription) => Number(a.index) - Number(b.index))
            .map((description: IBookingPropertyDescription) => {
                // Avoid text in double for checkIn and checkOut informations
                const textLines: string[] = (this.firstInformation.checkIn && description.types[0].xCode === EPropertyXCode.checkIn) ||
                (this.firstInformation.checkOut && description.types.some((type: IPropertyXCodeAndXApply) => type.xCode === EPropertyXCode.checkOut)) ?
                    description.textLines.slice(1) :
                    description.textLines;
                if (!textLines || textLines.length === 0) {
                    return {};
                }
                return {
                    header: description.types[0].xApply ?
                        `${description.title} - ${this.wording[description.types[0].xApply] || description.types[0].xApply}` :
                        description.title,
                    paragraph: textLines ?
                        textLines.map((text: string) => this.htmlizePipe.transform(text)) :
                        undefined,
                };
            })
            .reduce((articles: IArticle[], currentArticle: IArticle, index: number) => {
                if (index === 0) {
                    return [currentArticle];
                }
                if (!currentArticle.paragraph) {
                    return articles;
                }
                const lastArticle: IArticle = articles[articles.length - 1];
                if (currentArticle.header === lastArticle.header) {
                    lastArticle.paragraph = [...lastArticle.paragraph, ...currentArticle.paragraph];
                    return articles;
                }
                return [...articles, currentArticle];
            }, []);
    }

    public closeDialog(): void {
        this.dialog.closeAll();
    }

    public trackByFn(index: number): number {
        return index;
    }
}
